/**
 * alertsService.js
 * Gestiona umbrales clínicos configurables desde Firestore
 * y persistencia de alertas críticas por usuario.
 */

import { db } from "@/firebase";
import {
  doc, getDoc, setDoc, collection, addDoc, getDocs,
  query, where, updateDoc, serverTimestamp, Timestamp,
} from "firebase/firestore";

// ── Obtener emails de todos los admins desde Firestore ────────────────────────
async function getAdminEmails() {
  try {
    const snap = await getDocs(query(collection(db, "users"), where("role", "==", "admin")));
    return snap.docs.map((d) => d.data().email).filter(Boolean);
  } catch (_) { return []; }
}

// ── Obtener emails de los IHOs de una empresa ─────────────────────────────────
async function getIHOEmails(clientId) {
  if (!clientId) return [];
  try {
    const snap = await getDocs(query(
      collection(db, "users"),
      where("isIHO", "==", true),
      where("clientId", "==", clientId)
    ));
    return snap.docs.map((d) => d.data().email).filter(Boolean);
  } catch (_) { return []; }
}

// ── Umbrales por defecto (basados en evidencia AHA/ACC 2024, ADA, NEWS2) ──────
export const DEFAULT_THRESHOLDS = {
  paSistolica:  { info: 120, warning: 130, danger: 140, critical: 180 },
  paDiastolica: { warning: 80, danger: 90, critical: 120 },
  glucemia:     { hypoCritical: 54, hypo: 70, warning: 100, danger: 126, severe: 250 },
  oximetria:    { warning: 95, danger: 92, critical: 90 },
  // FaceHeart usa el Stress Index de Baevsky (NO es escala 0-100).
  // Rangos oficiales del SDK v5.9.3: <50 Low · 50-200 Normal · 200-350 Mild · 350-500 High · ≥500 Very High.
  stressIndex:  { info: 200, warning: 350, danger: 500 },
  imc:          { warning: 25, danger: 30, critical: 40 },
  sueno:        { warning: 7 },
};

// ── Cargar umbrales desde Firestore (con fallback a defaults) ─────────────────
export async function loadThresholds() {
  try {
    const snap = await getDoc(doc(db, "config", "alertThresholds"));
    if (snap.exists()) {
      const stored = snap.data() || {};
      // Migración: el stressIndex pasó de escala 0-100 a Baevsky (0-1000+).
      // Si lo guardado parece estar en la escala vieja (danger <= 100), descártalo
      // y usa los nuevos defaults para evitar falsas alertas "Muy alto".
      if (stored.stressIndex && typeof stored.stressIndex.danger === "number" && stored.stressIndex.danger <= 100) {
        delete stored.stressIndex;
      }
      return { ...DEFAULT_THRESHOLDS, ...stored };
    }
  } catch (_) { /* fallback a defaults */ }
  return DEFAULT_THRESHOLDS;
}

// ── Guardar umbrales editados desde Admin ─────────────────────────────────────
export async function saveThresholds(thresholds) {
  await setDoc(doc(db, "config", "alertThresholds"), thresholds, { merge: true });
}

// ── Cargar configuración OMS desde Firestore ──────────────────────────────────
export async function loadOmsConfig() {
  try {
    const snap = await getDoc(doc(db, "config", "omsQuestionnaire"));
    if (snap.exists()) {
      return snap.data();
    }
  } catch (_) { /* silenciar */ }
  return null;
}

const RAW_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL = RAW_BACKEND_URL === "http://localhost:8001" && window.location.hostname !== "localhost"
  ? ""
  : RAW_BACKEND_URL;
const DEDUP_HOURS = 24; // no repetir la misma alerta dentro de 24h
const PUSH_DEDUP_MS = DEDUP_HOURS * 3600 * 1000;

// Dedupe del push notification vía localStorage (independiente de Firestore index).
// Garantiza que solo se envía una notificación por tipo/severidad cada 24h,
// aunque el usuario entre al dashboard varias veces.
function shouldSendPush(uid, type) {
  if (typeof window === "undefined") return true;
  const key = `we360_push_sent_${uid}_${type}`;
  const last = parseInt(localStorage.getItem(key) || "0", 10);
  if (Date.now() - last < PUSH_DEDUP_MS) return false;
  return true;
}

function markPushSent(uid, type) {
  if (typeof window === "undefined") return;
  localStorage.setItem(`we360_push_sent_${uid}_${type}`, String(Date.now()));
}

// Dedupe para email de alerta — misma lógica que push pero clave diferente
function shouldSendAlertEmail(uid, type) {
  if (typeof window === "undefined") return true;
  const key = `we360_email_alert_${uid}_${type}`;
  const last = parseInt(localStorage.getItem(key) || "0", 10);
  return Date.now() - last >= PUSH_DEDUP_MS;
}

function markAlertEmailSent(uid, type) {
  if (typeof window === "undefined") return;
  localStorage.setItem(`we360_email_alert_${uid}_${type}`, String(Date.now()));
}

// ── Persistir alerta en Firestore con deduplicación ───────────────────────────
export async function persistAlert(uid, alert) {
  if (!uid || !["danger", "critical"].includes(alert.variant)) return;

  const alertType = alert.type || alert.text;

  // Verificar si ya existe alerta del mismo tipo en las últimas 24h (historial)
  // Filtramos en cliente para evitar requerir compound index Firestore.
  const cutoff = Date.now() - DEDUP_HOURS * 3600 * 1000;
  const alertsRef = collection(db, "users", uid, "alerts");
  let alreadyExistsInWindow = false;
  try {
    const existing = await getDocs(query(alertsRef, where("type", "==", alertType)));
    alreadyExistsInWindow = existing.docs.some((d) => {
      const t = d.data().triggeredAt;
      // serverTimestamp puede ser Timestamp object o null en cliente recién creado
      const ms = t?.toMillis ? t.toMillis() : (t?.seconds ? t.seconds * 1000 : 0);
      return ms >= cutoff;
    });
  } catch (_) { /* no bloquear por error de Firestore */ }

  if (!alreadyExistsInWindow) {
    // Expiración automática: 30 días desde el trigger
    const expiresAt = new Date(Date.now() + 30 * 24 * 3600 * 1000);
    const triageLevel = alert.level ?? (alert.variant === "critical" ? 3 : alert.variant === "danger" ? 2 : 1);
    await addDoc(alertsRef, {
      type:        alertType,
      level:       triageLevel,
      text:        alert.text,
      doctorText:  alert.doctorText || null,
      value:       alert.value ?? null,
      threshold:   alert.threshold ?? null,
      severity:    alert.variant,
      triggeredAt: serverTimestamp(),
      seen:        false,
      resolved:    false,
      resolvedAt:  null,
      expiresAt:   Timestamp.fromDate(expiresAt),
    });

  }

  // Enviar push para alertas críticas — dedupe por localStorage (no por Firestore index)
  if (alert.variant === "critical" && BACKEND_URL && shouldSendPush(uid, alertType)) {
    try {
      await fetch(`${BACKEND_URL}/api/push/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid,
          title: "WE360 — Alerta de Salud",
          body: alert.text,
          url: "/dashboard",
          critical: true,
        }),
      });
      markPushSent(uid, alertType);
    } catch (_) { /* silenciar errores de push */ }
  }

  // Enviar email de alerta crítica al usuario y a los admins — dedupe 24h
  if (alert.variant === "critical" && BACKEND_URL && shouldSendAlertEmail(uid, alertType)) {
    try {
      // Obtener datos del usuario desde Firestore
      const userSnap = await getDoc(doc(db, "users", uid));
      const userData = userSnap.exists() ? userSnap.data() : {};
      const userEmail = userData.email || userData.profile?.email || null;
      const userName = [userData.profile?.nombres, userData.profile?.apellidos].filter(Boolean).join(" ") || null;
      const userClientId = userData.clientId || null;

      // Obtener emails de admins e IHOs en paralelo
      const [adminEmails, ihoEmails] = await Promise.all([
        getAdminEmails(),
        getIHOEmails(userClientId),
      ]);

      // Solo notificamos a IHO y admin — no al usuario (evitar estrés innecesario)
      if (adminEmails.length > 0 || ihoEmails.length > 0) {
        await fetch(`${BACKEND_URL}/api/email/notify-alert`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_email: userEmail,   // Se usa como referencia de identidad en el email al IHO/admin
            user_name: userName,
            uid,
            alerts: [{ type: alertType, text: alert.text, value: alert.value ?? null }],
            admin_emails: adminEmails,
            iho_emails: ihoEmails,
            skip_user_email: true,   // No enviar email al usuario
          }),
        });
        markAlertEmailSent(uid, alertType);
      }
    } catch (_) { /* silenciar errores de email */ }
  }
}

// ── Enviar push de prueba (para testing desde Admin) ──────────────────────────
export async function sendTestPush(uid) {
  if (!uid || !BACKEND_URL) return { ok: false, sent: 0, error: "Backend no configurado" };
  const res = await fetch(`${BACKEND_URL}/api/push/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      uid,
      title: "WE360 — Notificación de prueba",
      body: "Las notificaciones push están funcionando correctamente.",
      url: "/dashboard",
    }),
  });
  let data;
  try { data = await res.json(); } catch { data = {}; }
  if (!res.ok) {
    return { ok: false, sent: 0, error: data.detail || data.error || `HTTP ${res.status}` };
  }
  return data;
}

// ── Obtener alertas no vistas del usuario ─────────────────────────────────────
export async function getUnseenAlerts(uid) {
  if (!uid) return [];
  try {
    const q = query(collection(db, "users", uid, "alerts"), where("seen", "==", false));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (_) { return []; }
}

// ── Marcar alerta(s) como vistas ──────────────────────────────────────────────
export async function markAlertsSeen(uid, alertIds) {
  if (!uid || !alertIds?.length) return;
  await Promise.all(alertIds.map((id) =>
    updateDoc(doc(db, "users", uid, "alerts", id), { seen: true })
  ));
}

// ── Resolver alertas de Firestore para tipos que ya están en rango normal ──────
// Se llama desde Measurement.js después de guardar nuevos valores normales.
export async function resolveAlertsByType(uid, types) {
  if (!uid || !types?.length) return;
  try {
    const alertsRef = collection(db, "users", uid, "alerts");
    // Traer todas las alertas no resueltas de esos tipos
    const snap = await getDocs(alertsRef);
    const toResolve = snap.docs.filter((d) => {
      const data = d.data();
      return types.includes(data.type) && !data.resolved;
    });
    await Promise.all(
      toResolve.map((d) =>
        updateDoc(doc(db, "users", uid, "alerts", d.id), {
          resolved:   true,
          resolvedAt: serverTimestamp(),
          seen:       true,
        })
      )
    );
  } catch (_) { /* silenciar errores de resolución */ }
}

// ── Determina qué tipos de alerta se resuelven dado un conjunto de nuevos valores ─
// Devuelve un array de types (ej. ["pa", "glucemia"]) que están en rango normal.
export function getNormalAlertTypes(fields, thresholds = DEFAULT_THRESHOLDS) {
  const t = thresholds;
  const resolved = [];

  const pas = parseFloat(fields.paSistolica);
  const pad = parseFloat(fields.paDiastolica);
  if (!isNaN(pas) && !isNaN(pad)) {
    const isCritical = pas >= (t.paSistolica?.critical ?? 180) || pad >= (t.paDiastolica?.critical ?? 120);
    const isDanger   = pas >= (t.paSistolica?.danger ?? 140)   || pad >= (t.paDiastolica?.danger ?? 90);
    const isWarning  = pas >= (t.paSistolica?.warning ?? 130)  || pad >= (t.paDiastolica?.warning ?? 80);
    if (!isCritical && !isDanger && !isWarning) resolved.push("pa");
  }

  const glu = parseFloat(fields.glucemia);
  if (!isNaN(glu)) {
    const isAlert = glu < (t.glucemia?.hypo ?? 70) || glu >= (t.glucemia?.warning ?? 100);
    if (!isAlert) resolved.push("glucemia");
  }

  const oxi = parseFloat(fields.oximetria);
  if (!isNaN(oxi)) {
    if (oxi >= (t.oximetria?.warning ?? 95)) resolved.push("oximetria");
  }

  const si = parseFloat(fields.stressIndex);
  if (!isNaN(si)) {
    if (si < (t.stressIndex?.info ?? 200)) resolved.push("stressIndex");
  }

  const hr = parseFloat(fields.heartRate);
  if (!isNaN(hr)) {
    if (hr >= 50 && hr <= 100) resolved.push("heartRate");
  }

  return resolved;
}
